// web/src/AddressSearch.jsx
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { resultPathFromBanId } from "./utils/resultUrl";

// localStorage fallback history (for guests and UX)
const LS_KEY = "edl_history";
function readLocalHistory() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

export default function AddressSearch() {
  const { user } = useAuth(); // know if we’re logged in

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [history, setHistory] = useState([]); // last visited (per-user or guest)

  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    // Close suggestions on outside click
    const onDocMouseDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDocMouseDown);
    return () => window.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  // load history:
  // - if logged in: ONLY server /api/me/history (per-user)
  // - if guest: ONLY localStorage
  const fetchHistory = async () => {
    const isLoggedIn = !!user;

    // Guest: local history only
    const local = !isLoggedIn
      ? readLocalHistory()
          .map((x) => ({
            ban_id: x?.ban_id,
            label: x?.label || "",
            viewed_at: x?.ts ? new Date(x.ts).toISOString() : null,
            _source: "local",
          }))
          .filter((x) => x.ban_id)
      : [];

    // Logged-in: server history only
    let server = [];
    if (isLoggedIn) {
      try {
        const r = await fetch("/api/me/history", { credentials: "include" });
        if (r.ok) {
          const arr = await r.json();
          server = (Array.isArray(arr) ? arr : [])
            .map((x) => ({
              ban_id: x?.ban_id,
              label: x?.label || "",
              viewed_at: x?.viewed_at || null,
              _source: "server",
            }))
            .filter((x) => x.ban_id);
        }
      } catch {
        // if error, keep server []
      }
    }

    // Merge logic kept for symmetry / future-proof,
    // but in practice:
    // - guest => merged === local
    // - logged-in => merged === server
    const map = new Map();
    [...server, ...local].forEach((item) => {
      const prev = map.get(item.ban_id);
      if (!prev) {
        map.set(item.ban_id, item);
      } else {
        const tPrev = prev.viewed_at ? Date.parse(prev.viewed_at) : 0;
        const tNew = item.viewed_at ? Date.parse(item.viewed_at) : 0;
        if (tNew > tPrev) map.set(item.ban_id, item);
      }
    });

    const merged = Array.from(map.values())
      .sort((a, b) => Date.parse(b.viewed_at || 0) - Date.parse(a.viewed_at || 0))
      .slice(0, 10);

    setHistory(merged);
  };

  // Refetch when component mounts or when user changes (login/logout)
  useEffect(() => {
    (async () => {
      await fetchHistory();
    })();
  }, [user]);

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      // don’t auto-close; we may want to show history
      setOpen(true);
      setHighlight(-1);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);

    try {
      const res = await fetch(`/api/autocomplete?q=${encodeURIComponent(q)}&limit=7`, {
        signal: abortRef.current.signal,
      });
      if (!res.ok) throw new Error("network");
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
      setOpen(true);
      setHighlight(-1);
    } catch (e) {
      if (e.name !== "AbortError") console.error(e);
      setResults([]);
      setOpen(true); // keep open to show history fallback
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(v.trim()), 250);
  };

  const openAddress = (ban_id) => {
    if (!ban_id) return;
    window.location.href = resultPathFromBanId(ban_id);
  };

  const selectItem = (item) => {
    if (!item?.ban_id) return;
    window.location.href = resultPathFromBanId(item.ban_id);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (
        e.key === "ArrowDown" &&
        (results.length > 0 || (query.trim().length === 0 && history.length > 0))
      ) {
        setOpen(true);
      }
      return;
    }
    if (results.length === 0) return; // keyboard nav only for autocomplete list
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((p) => Math.min(p + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[highlight === -1 ? 0 : highlight];
      if (item) selectItem(item);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const listId = "addr-suggestions";
  const activeId = highlight >= 0 ? `addr-option-${highlight}` : undefined;

  const displayLabel = (r) =>
    r.label ||
    r.name ||
    r.text ||
    r.display_name ||
    r.address ||
    (r.ban_id?.split(" ").slice(0, -2).join(" ")) ||
    "";

  const displayContext = (r) =>
    r.context ||
    [r.postcode, r.city, r.commune, r.departement].filter(Boolean).join(" • ");

  const highlightMatch = (text, q) => {
    if (!text || !q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;
    return (
      <span>
        {text.slice(0, i)}
        <strong>{text.slice(i, i + q.length)}</strong>
        {text.slice(i + q.length)}
      </span>
    );
  };

  const showHistoryFallback =
    open &&
    !loading &&
    query.trim().length === 0 &&
    results.length === 0 &&
    history.length > 0;

  return (
    <div
      ref={wrapRef}
      className="addr-wrap"
      style={{ position: "relative", maxWidth: 700 }}
    >
      {/* Scoped, modern styles */}
      <style>{`
        .addr-wrap{
          isolation: isolate;
          z-index: 99999;
        }
        .addr-label{
          display:block; margin-bottom:8px; font-weight:600;
        }
        .addr-field{
          position:relative; display:flex; align-items:center; gap:10px;
          background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9));
          backdrop-filter: blur(14px);
          border-radius: 999px;
          padding: 12px 14px 12px 48px;
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          transition: box-shadow .2s ease, border-color .2s ease, transform .06s ease;
        }
        .addr-field:focus-within{
          box-shadow: 0 12px 36px rgba(59,130,246,0.25), 0 0 0 3px rgba(59,130,246,0.25);
          border-color: rgba(59,130,246,0.35);
          transform: translateY(-1px);
        }
        .addr-input{
          width:100%; font-size:1rem; line-height:1.2;
          border:none; outline:none; background:transparent; color:#0f172a;
        }
        .addr-input::placeholder{ color:#94a3b8 }
        .addr-icon{
          position:absolute; left:14px; top:50%; transform:translateY(-50%);
          width:22px; height:22px; opacity:.7; pointer-events:none;
        }
        .addr-clear{
          border:none; background:transparent; cursor:pointer;
          width:28px; height:28px; border-radius:999px; display:flex; align-items:center; justify-content:center;
        }
        .addr-clear:hover{ background:rgba(15,23,42,0.06) }

        .addr-spinner{
          width:18px;height:18px;border-radius:50%;
          border:2px solid rgba(15,23,42,0.15); border-top-color:rgba(59,130,246,1);
          animation:spin .8s linear infinite;
        }
        @keyframes spin{ to{ transform:rotate(360deg) } }

        /* Dropdown */
        .addr-list{
          position:absolute; left:0; right:0; top: calc(100% + 8px);
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(18px);
          border:1px solid rgba(0,0,0,0.06);
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.18);
          max-height: 340px; overflow:auto; padding:6px;
          z-index: 2147483647;
          list-style:none; margin:0;
        }
        .addr-item{
          display:flex; gap:10px; padding:10px 12px; border-radius:12px; cursor:pointer;
          color:#0f172a; font-size:0.98rem; align-items:flex-start;
        }
        .addr-item:hover, .addr-item[aria-selected="true"]{
          background: rgba(59,130,246,0.10);
        }
        .addr-line1{ font-weight:600; }
        .addr-line2{ color:#64748b; font-size:0.9rem; }

        .addr-section-title{
          margin:6px 8px 2px; font-size:12px; color:#64748b; font-weight:800; text-transform:uppercase;
        }

        @media print{
          .addr-wrap { display:none !important; }
        }
      `}</style>

      <label htmlFor="addr-search" className="addr-label">
        Entrez une adresse complète, un nom de rue, un code postal ...
      </label>

      <div
        className="addr-field"
        role="combobox"
        aria-expanded={open}
        aria-owns={listId}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-activedescendant={activeId}
      >
        {/* Magnifier icon */}
        <svg
          className="addr-icon"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <input
          id="addr-search"
          value={query}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setOpen(true);
            if (!query && history.length === 0) fetchHistory();
          }}
          placeholder="Ex. « 55 Rue du Faubourg Saint-Honoré »"
          autoComplete="off"
          className="addr-input"
          aria-autocomplete="list"
          aria-controls={listId}
        />

        {query && !loading && (
          <button
            type="button"
            className="addr-clear"
            aria-label="Effacer la recherche"
            title="Effacer"
            onClick={() => {
              setQuery("");
              setResults([]);
              setOpen(true);
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}

        {loading && <div className="addr-spinner" aria-hidden="true" />}
      </div>

      {open && (
        <>
          {/* Autocomplete results */}
          {results.length > 0 && (
            <ul id={listId} role="listbox" className="addr-list">
              {results.map((r, i) => {
                const label = displayLabel(r);
                const context = displayContext(r);
                return (
                  <li
                    key={r.ban_id ?? i}
                    id={`addr-option-${i}`}
                    role="option"
                    aria-selected={highlight === i}
                    className="addr-item"
                    onMouseEnter={() => setHighlight(i)}
                    onMouseDown={(ev) => ev.preventDefault()} // keep focus
                    onClick={() => selectItem(r)}
                  >
                    <span aria-hidden="true">📍</span>
                    <div>
                      <div className="addr-line1">
                        {highlightMatch(label, query)}
                      </div>
                      {context && (
                        <div className="addr-line2">{context}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* History fallback when input empty and no autocomplete */}
          {showHistoryFallback && (
            <ul className="addr-list" aria-label="Historique">
              <li className="addr-section-title">Historique récent</li>
              {history.map((h, i) => {
                const label =
                  h.label ||
                  (h.ban_id?.split(" ").slice(0, -2).join(" ")) ||
                  h.ban_id;
                return (
                  <li
                    key={`${h.ban_id}-${i}`}
                    className="addr-item"
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => openAddress(h.ban_id)}
                  >
                    <span aria-hidden="true">📍</span>
                    <div className="addr-line1">{label}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}